import React from "react";

import { faClock } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Link,
  Pagination,
  Select,
  Spinner,
  Table,
  Typography,
} from "@samskip/frontend-components";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { CommonConstants } from "@assets/constants";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import { SelectOption } from "@assets/types";
import { LabelMapperUtilities } from "@assets/utilities";
import { classNames } from "@assets/utilities/classNameUtilities";
import { listToSelectOptionMapper } from "@assets/utilities/labelMapperUtils";
import { BookingTemplateActionMenu } from "@components";
import {
  filterBookingTemplateWithPageSize,
  filterBookingTemplateWithPagination,
  bookingTemplatesState as bookingTemplatesState,
} from "@state/slices/bookingTemplateSlice";

const BookingTemplatesTable = () => {
  const { t } = useTranslation();

  const bookingTemplateHeaders = [
    {
      id: "No",
      name: t("LABEL_ABBREV_NUMBER"),
      className: "w-[5.5rem] text-right",
    },
    {
      id: "Name",
      name: t("LABEL_TEMPLATE_NAME"),
    },
    {
      id: "Shipper",
      name: t("LABEL_SHIPPER"),
    },
    {
      id: "Consignee",
      name: t("LABEL_CONSIGNEE"),
    },
    {
      id: "Last used",
      name: t("LABEL_LAST_USED"),
    },
  ];

  const resultsPerPageOptions = listToSelectOptionMapper([10, 25, 50, 100]);

  const {
    templates: bookingTemplates,
    isLoading: areBookingTemplatesLoading,
    filters,
    totalCount,
  } = useAppSelector(bookingTemplatesState);

  const dispatch = useAppDispatch();

  const bookingTemplateRows = bookingTemplates.map((bookingTemplate) => {
    return {
      bookingTemplateId: bookingTemplate.Id,
      bookingTemplateName: bookingTemplate.Name,
      bookingTemplatePartner: bookingTemplate.PartnerCode,
      cells: [
        bookingTemplate.Id,
        bookingTemplate.Name,
        bookingTemplate.ShipperName,
        bookingTemplate.ConsigneeName,
        <div className="flex gap-1.5" key={bookingTemplate.Id}>
          <FontAwesomeIcon className="text-neutral-600 mt-0.5" icon={faClock} />
          <div className="flex flex-col gap-1.5 items-baseline text-neutral-800">
            {bookingTemplate.LastUsedDate ? (
              <>
                <p>
                  {format(
                    new Date(bookingTemplate.LastUsedDate),
                    CommonConstants.DATE_FORMAT
                  )}
                </p>
                <p> {bookingTemplate.LastUsedBy}</p>
              </>
            ) : (
              <p> {t("LABEL_NOT_PREVIOUSLY_USED")}</p>
            )}
          </div>
        </div>,
      ],
    };
  });

  const handleResultsPerPageChange = (selectedValue: SelectOption<number>) => {
    dispatch(filterBookingTemplateWithPageSize(selectedValue.value));
  };

  const handlePaginationChange = (page: number) => {
    dispatch(filterBookingTemplateWithPagination(page));
  };

  if (areBookingTemplatesLoading) {
    return <Spinner fullWidth />;
  }

  if (bookingTemplates.length === 0) {
    return null;
  }

  return (
    <>
      <Table id="template-table" isBordered>
        <Table.Header>
          <Table.HeaderRow>
            {bookingTemplateHeaders.map((header) => (
              <Table.HeaderCell
                className={classNames(header.className)} // Apply the headerClassName to the header cell
                key={header.id}
              >
                {header.name}
              </Table.HeaderCell>
            ))}
            <Table.HeaderCell className="w-16" />
            <Table.HeaderCell className="w-16" />
          </Table.HeaderRow>
        </Table.Header>
        <Table.Body>
          {bookingTemplateRows.map((row) => (
            <Table.Row isStriped key={row.bookingTemplateId} size="large">
              {row.cells.map((cell, cellIndex) => {
                return cell ? (
                  <Table.Cell
                    className={
                      bookingTemplateHeaders[cellIndex] &&
                      bookingTemplateHeaders[cellIndex].className
                    }
                    ellipsis="normal"
                    key={`${row.bookingTemplateId}-cell-${cellIndex}`}
                  >
                    {cell}
                  </Table.Cell>
                ) : (
                  <Table.Cell
                    className="text-neutral-700"
                    key={`${row.bookingTemplateId}-cell-${cellIndex}`}
                  >
                    {t("LABEL_NOT_PROVIDED")}
                  </Table.Cell>
                );
              })}
              <Table.Cell>
                <Link
                  href={`${process.env.MainServiceWeb}/shipments/create/${row.bookingTemplateId}/book-template`}
                >
                  {t("LABEL_BOOK")}
                </Link>
              </Table.Cell>
              <Table.Cell>
                <BookingTemplateActionMenu
                  bookingTemplateId={row.bookingTemplateId}
                  bookingTemplateName={row.bookingTemplateName}
                  bookingTemplatePartner={row.bookingTemplatePartner}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <div className="flex items-baseline justify-between">
        <div className="flex gap-2">
          <Typography variant="label">
            {`${t("LABEL_RESULTS_PER_PAGE")}:`}
          </Typography>
          <Select
            defaultValue={resultsPerPageOptions[0]}
            id="page-size"
            isSearchable={false}
            onChange={handleResultsPerPageChange}
            options={resultsPerPageOptions}
            portaling
            value={LabelMapperUtilities.singleValueToSelectOptionMapper(
              filters.pageSize
            )}
            variant="compact"
          />
        </div>
        <Pagination
          onPageChange={handlePaginationChange}
          page={filters.page}
          pageSize={filters.pageSize}
          totalCount={totalCount}
        />
      </div>
    </>
  );
};

export default BookingTemplatesTable;
